import React from "react";
import { StreamerStatus } from "@pureweb/platform-sdk";
import { ErrorView } from "../ErrorView/ErrorView";
import { EmbeddedView } from "../EmbeddedView/EmbeddedView";
import { LaunchPanel } from "../LaunchPanel/LaunchPanel";
import { ClientJson } from "../../types";

interface AppViewProps {
  // Client and configuration
  client: ClientJson;
  clientOptions: any;

  // Status and state
  streamerStatus: StreamerStatus;
  status: any;
  loading: boolean;
  modelDefinitionUnavailable: boolean;
  availableModels: any[] | undefined;

  // Streams and emitter
  videoStream: MediaStream;
  emitter: any;

  // Handlers
  onLaunch: () => void;
}

export const AppView: React.FC<AppViewProps> = ({
  client,
  clientOptions,
  streamerStatus,
  status,
  loading,
  modelDefinitionUnavailable,
  availableModels,
  videoStream,
  emitter,
  onLaunch,
}) => {
  // Error states
  if (modelDefinitionUnavailable) {
    return (
      <ErrorView>
        <span>The model that you have requested does not exist</span>
      </ErrorView>
    );
  }

  if (streamerStatus === StreamerStatus.Disconnected) {
    return (
      <ErrorView>
        <h2>Disconnected from stream</h2>
      </ErrorView>
    );
  }

  if (streamerStatus === StreamerStatus.Failed) {
    return (
      <ErrorView>
        <h2>Failure during stream</h2>
        <h2>Please refresh to request a new session</h2>
      </ErrorView>
    );
  }

  if (streamerStatus === StreamerStatus.Withdrawn) {
    return (
      <ErrorView>
        <h2>Streamer contribution withdrawn</h2>
      </ErrorView>
    );
  }

  // Loading/Active state
  if (loading) {
    return (
      <EmbeddedView
        VideoStream={videoStream}
        StreamerStatus={streamerStatus}
        LaunchRequestStatus={status}
        InputEmitter={emitter}
        UseNativeTouchEvents={clientOptions.UseNativeTouchEvents!}
        UsePointerLock={clientOptions.UsePointerLock!}
        PointerLockRelease={clientOptions.PointerLockRelease!}
      />
    );
  }

  // Launch panel conditions
  const shouldShowLaunchPanel =
    (clientOptions.LaunchType !== "local" && !availableModels) ||
    availableModels?.length;

  if (shouldShowLaunchPanel) {
    return (
      <LaunchPanel
        logoSrc={client.logo}
        description={client.description}
        subheading={client.subheading}
        title={client.title}
        isLoading={clientOptions.LaunchType !== "local" && !availableModels}
        onPlayButtonClick={onLaunch}
      />
    );
  }

  // No models available
  if (clientOptions.LaunchType !== "local" && !availableModels?.length) {
    return (
      <ErrorView>
        <h2>No models are currently available in this environment.</h2>
      </ErrorView>
    );
  }

  return null;
};
